/**
 * Node type to legacy kind mappings
 * This is the single source of truth for node type to legacy kind mapping
 */
import type { LegacyNodeKind } from '../legacyNodes'
import { NodeType } from '../reactFlowTypes'

/**
 * Type mapping: React Flow node type → Legacy kind
 * This is the single source of truth for node type to legacy kind mapping
 */
export const NODE_TYPE_TO_KIND_MAP: Record<string, LegacyNodeKind> = {
  start: 'start_node',
  [NodeType.Transfer]: 'direct_line',
  [NodeType.TransferAndContinue]: 'simple_dial',
  [NodeType.TransferAndRollover]: 'transfer_rollover',
  [NodeType.Queue]: 'queue',
  [NodeType.HangUp]: 'hang_up',
  [NodeType.Schedule]: 'schedule',
  [NodeType.AutoAttendant]: 'auto_attendant',
  [NodeType.Webhook]: 'webhook',
  [NodeType.InteractiveWebhook]: 'interactive_webhook',
  [NodeType.SmsNotification]: 'sms',
  [NodeType.ExternalTransfer]: 'external_number',
  external_number: 'external_number', // Legacy alias, kept for compatibility
  [NodeType.LeaveVoicemail]: 'leave_voicemail',
  [NodeType.DayNight]: 'day_night',
  [NodeType.DialByName]: 'dial_name',
  [NodeType.SoundClip]: 'sound_clip',
  [NodeType.FaxDetect]: 'fax_detect',
  [NodeType.Wait]: 'wait',
  [NodeType.JumpToSwitchboard]: 'jump_to_dial_plan',
  jump_to_dial_plan: 'jump_to_dial_plan', // Legacy alias, kept for compatibility
  [NodeType.ConferenceRoom]: 'conference_room',
  [NodeType.CallDistribution]: 'call_distribution',
  [NodeType.ChangeCallerId]: 'change_caller_id',
  [NodeType.AiAgentV1]: 'ai_agent_v1',
} as const

/**
 * Helper function to invert a mapping from NodeType to LegacyNodeKind
 * Handles special cases where multiple node types map to the same legacy kind
 * or where additional legacy kinds need to be added (like 'transfer')
 */
function invertNodeTypeToKindMap(
  forwardMap: Record<string, LegacyNodeKind>,
): Partial<Record<LegacyNodeKind, NodeType | 'start'>> {
  const inverted: Partial<Record<LegacyNodeKind, NodeType | 'start'>> = {}

  // Invert the forward map
  for (const [nodeType, legacyKind] of Object.entries(forwardMap)) {
    // For 'start', use the string literal
    if (nodeType === 'start') {
      inverted[legacyKind] = 'start'
    } else {
      // For other types, try to find the corresponding NodeType enum value
      // Handle both enum values and string literals
      const nodeTypeEnum = Object.values(NodeType).find(
        (val) => val === nodeType,
      ) as NodeType | undefined
      if (nodeTypeEnum) {
        // Only set if not already set (prefer first occurrence)
        if (!inverted[legacyKind]) {
          inverted[legacyKind] = nodeTypeEnum
        }
      }
    }
  }

  return inverted
}

/**
 * Reverse mapping: Legacy kind → React Flow node type
 * Derived from NODE_TYPE_TO_KIND_MAP to prevent drift
 * Includes special cases for legacy kinds that need additional mappings
 */
export const KIND_TO_NODE_TYPE_MAP: Record<LegacyNodeKind, NodeType | 'start'> =
  (() => {
    // Start with the inverted forward map
    const baseMap = invertNodeTypeToKindMap(NODE_TYPE_TO_KIND_MAP)

    // Add special cases that aren't covered by simple inversion
    // 'transfer' is a legacy kind that also maps to Transfer (same as 'direct_line')
    baseMap.transfer = NodeType.Transfer

    // Validate that all legacy kinds are covered
    const allLegacyKinds: LegacyNodeKind[] = [
      'start_node',
      'simple_dial',
      'queue',
      'schedule',
      'day_night',
      'transfer_rollover',
      'auto_attendant',
      'sound_clip',
      'wait',
      'hang_up',
      'leave_voicemail',
      'dial_name',
      'jump_to_dial_plan',
      'conference_room',
      'external_number',
      'fax_detect',
      'webhook',
      'interactive_webhook',
      'call_distribution',
      'sms',
      'change_caller_id',
      'direct_line',
      'transfer',
      'ai_agent_v1',
    ]

    const missingKinds = allLegacyKinds.filter((kind) => !baseMap[kind])
    if (missingKinds.length > 0) {
      throw new Error(
        `Missing mappings for legacy kinds: ${missingKinds.join(', ')}`,
      )
    }

    return baseMap as Record<LegacyNodeKind, NodeType | 'start'>
  })()

/**
 * All legacy node types in order (matching legacy code)
 */
export const ALL_LEGACY_NODE_TYPES: LegacyNodeKind[] = [
  'start_node',
  'simple_dial',
  'direct_line',
  'call_distribution',
  'auto_attendant',
  'external_number',
  'ai_agent_v1',
  'leave_voicemail',
  'schedule',
  'day_night',
  'dial_name',
  'sound_clip',
  'wait',
  'hang_up',
  'jump_to_dial_plan',
  'conference_room',
  'queue',
  'transfer_rollover',
  'change_caller_id',
  'webhook',
  'fax_detect',
  'interactive_webhook',
  'sms',
] as const
